import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { Button, PinHolder, toast, Text, FlexWrapper } from '@intigral/shared';
import { get, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    B2C_ACCOUNT_RETRIEVAL_OTP_LENGTH,
    KIDSMODE,
    NAVIGATION,
    parentalPinManagementConstants,
    ROUTE_CONSTANT,
    screenDetailTypes,
    STORE_NAME,
} from '../../config/constants';
import h from '../../helpers/common';
import {
    clearParentalLockOtpResendCooldown,
    canUseParentalLockResetOtpFlow,
    getParentalLockOtpFallbackScreen,
    clearVerifiedOtpStorage,
    clearVerifiedParentalPinStorage,
    clearVerificationGuidStorage,
    getMaskedOtpContact,
    getParentalLockOtpContact,
    getParentalLockOtpResendCooldownRemainingSec,
    getParentalLockOtpToastErrorCode,
    isCannotGenerateParentalLockOtpError,
    getParentalLockOtpResendCooldownSec,
    persistParentalLockOtpResendCooldown,
    readVerificationGuid,
    readVerifiedOtp,
    readVerifiedParentalPin,
    storeVerifiedOtp,
    storeVerifiedParentalPin,
    storeVerificationGuid,
} from '../../helpers/parentalLockOtp';
import url from '../../helpers/url';
import user from '../../helpers/user';
import {
    createPinMutationFn,
    validatePinMutationFn,
    useUpdateParentalPinMutationFn,
    useGetParentalPinMutationFn,
    useAccountRetrievalGenerateOTPMutationFn,
    useAccountRetrievalVerifyOTPMutationFn,
    useResetParentalLockGenerateOTPMutationFn,
    useResetParentalLockVerifyOTPMutationFn,
    useResetParentalLockWithOTPMutationFn,
    normalizeVerifiedParentalLockPin,
    ParentalPinStep,
} from '../../hooks/useParentalPinManagement';
import { selectProfileDone } from '../../store/reducers/profileSlice';
import { __ } from '../../translations';
import PasswordPopup from '../elements/PasswordPopup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IProfileService } from '@intigral/domain/lib/application/services/ProfileService';
import { getUserInfo } from '../../utils/user';
import { useProfileList } from '../../utils/useUserProfile';

import withPresetWidth from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import { HeaderWcp, PinVerification } from '@intigral/ui';
import { useProfileExit, isParentalPinRequired } from '../../hooks/useProfileExit';

const FlexWidth = withPresetWidth(FlexWrapper);

const toastCmsError = (errCode) => {
    if (!errCode) {
        return;
    }
    const localizedKey = `err_msg_${errCode}`;
    let localizedError = __(localizedKey);
    if (localizedKey === localizedError) localizedError = __('general_error_message');
    toast(localizedError, 'error');
};

export const ParentalPinManagementForm = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [inputValue, setInputValue] = useState('');
    const [comesFromWhichScreen, setComesFromWhichScreen] = useState(null);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [buttonText, setButtonText] = useState('');
    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const [userPassword, setUserPassword] = useState(false);
    const [passwordValidated, setPasswordValidated] = useState(false);
    const [OTPLength, setOTPLength] = useState(4);
    const [step, setStepValue] = useState(null);
    const [errMsg, setErrMsg] = useState(null);
    const [errCode, setErrCode] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [pin, setPin] = useState(null);
    const [resetOtpValue, setResetOtpValue] = useState('');
    const [resetOtpFocus, setResetOtpFocus] = useState(false);
    const remainingCooldown = getParentalLockOtpResendCooldownRemainingSec();
    const [otpGenerateKey, setOtpGenerateKey] = useState(remainingCooldown > 0 ? 1 : null);
    const [otpResendCooldownSec, setOtpResendCooldownSec] = useState(remainingCooldown);

    const verificationGuidRef = useRef(readVerificationGuid());
    const verifiedOtpRef = useRef(readVerifiedOtp());
    const verifiedParentalPinRef = useRef(readVerifiedParentalPin());
    const isOtpResendRef = useRef(false);
    const otpGenerateStartedRef = useRef(Boolean(verificationGuidRef.current));

    // `currentScreenType` variable will contain only 4 values (add, change, validate, not-found)
    const [currentScreenType, setCurrentScreenType] = useState(null);

    // const { isLoading, errCode, errMsg, successMsg, canRedirect, step, response, verificationGuid } = useSelector(
    //     (state: IStore) => state.parentalPin
    // );

    //TODO: remove the slice completely and use the hooks directly

    const dispatch = useDispatch();

    const isB2C = user.getUserSegment() === 'B2C';
    const isB2B = user.getUserSegment() === 'B2B';
    const isMobilityUser = isB2C || user.getUserSegment() === 'B2B2C';

    const getVerificationGuid = () => {
        return verificationGuidRef.current || readVerificationGuid();
    };

    const saveVerificationGuid = (guid) => {
        verificationGuidRef.current = guid;
        storeVerificationGuid(guid);
    };

    const removeVerificationGuid = () => {
        verificationGuidRef.current = null;
        otpGenerateStartedRef.current = false;
        clearVerificationGuidStorage();
        clearParentalLockOtpResendCooldown();
        setOtpResendCooldownSec(0);
        setOtpGenerateKey(null);
    };

    const saveVerifiedOtp = (otp) => {
        verifiedOtpRef.current = otp;
        storeVerifiedOtp(otp);
    };

    const removeVerifiedOtp = () => {
        verifiedOtpRef.current = null;
        clearVerifiedOtpStorage();
    };

    const saveVerifiedParentalPin = (parentalLockPin) => {
        const normalizedPin = normalizeVerifiedParentalLockPin(parentalLockPin);
        if (!normalizedPin) {
            return;
        }
        verifiedParentalPinRef.current = normalizedPin;
        storeVerifiedParentalPin(normalizedPin);
    };

    const removeVerifiedParentalPin = () => {
        verifiedParentalPinRef.current = null;
        clearVerifiedParentalPinStorage();
    };

    const getVerifiedParentalPin = () => {
        return verifiedParentalPinRef.current || readVerifiedParentalPin();
    };

    // const [canRedirect, setCanRedirect] = useState(false);
    const { data: profileList } = useProfileList(getUserInfo());

    const { clearSelectedProfile, exitKidsMode } = useProfileExit();

    const client = useQueryClient();

    const createPinMutation = useMutation({
        mutationFn: createPinMutationFn,
        onSuccess: (data: any) => {
            setSuccessMsg(data.message);
            setStepValue(ParentalPinStep.CREATE_FULFILLED);
            redirectUser();
            setPin(data.pin);
        },
        onError: (error: any) => {
            setStepValue(ParentalPinStep.CREATE_REJECTED);
            setErrMsg(null);
            const toastErrorCode = getParentalLockOtpToastErrorCode(error);
            setErrCode(toastErrorCode);
            toastCmsError(toastErrorCode);
        },
    });
    const { mutate: createPin, isPending: isCreating } = createPinMutation;

    const validatePinMutation = useMutation({
        mutationFn: validatePinMutationFn,
        onSuccess: (data: any) => {
            setSuccessMsg(data.message);
            setStepValue(ParentalPinStep.VALIDATE_FULFILLED);
            redirectUser(ParentalPinStep.VALIDATE_FULFILLED);
        },
        onError: (error: any) => {
            setStepValue(ParentalPinStep.VALIDATE_REJECTED);
            setErrMsg(error?.message);
            setErrCode(error?.code);
            toastCmsError(error?.code);
        },
    });
    const { mutate: validatePin, isPending: isValidating } = validatePinMutation;

    const updatePinMutation = useMutation({
        mutationFn: useUpdateParentalPinMutationFn,
        onSuccess: (data: any) => {
            setSuccessMsg(data.message);
            setStepValue(ParentalPinStep.UPDATE_FULFILLED);
            setPin(data.pin);
            redirectUser();
        },
        onError: (error: any) => {
            setStepValue(ParentalPinStep.UPDATE_REJECTED);
            setErrMsg(null);
            const toastErrorCode = getParentalLockOtpToastErrorCode(error);
            setErrCode(toastErrorCode);
            toastCmsError(toastErrorCode);
        },
    });
    const { mutate: updatePin, isPending: isUpdating } = updatePinMutation;

    const getPinMutation = useMutation({
        mutationFn: useGetParentalPinMutationFn,
        onSuccess: (data: any) => {
            // setSuccessMsg(data.message);
            setStepValue(ParentalPinStep.GET_FULFILLED);
            setPin(data);
            if (currentScreenType === parentalPinManagementConstants.CHANGE) {
                setCurrentScreenType(parentalPinManagementConstants.ACCOUNT_RETRIEVAL_PIN_VIEW);
            } else {
                redirectUser();
            }
        },
        onError: (error: any) => {
            setStepValue(ParentalPinStep.UPDATE_REJECTED);
            setErrMsg(error?.message);
            setErrCode(error?.code);
            toastCmsError(error?.code);
        },
    });
    const { mutate: getPin, isPending: isLoadingGetPin, data: pinData } = getPinMutation;

    const generatePinOTPMutation = useMutation({
        mutationFn: useAccountRetrievalGenerateOTPMutationFn,
        onSuccess: (data: any) => {
            setSuccessMsg(data.message);
            setStepValue(ParentalPinStep.GENERATE_OTP_FULFILLED);
            redirectUser(ParentalPinStep.GENERATE_OTP_FULFILLED);
        },
        onError: (error: any) => {
            setStepValue(ParentalPinStep.GENERATE_OTP_REJECTED);
            setErrMsg(null);
            const toastErrorCode = getParentalLockOtpToastErrorCode(error);
            setErrCode(toastErrorCode);
            toastCmsError(toastErrorCode);
        },
    });
    const { mutate: generateOTP, data: generateOtpData, isPending: isGeneratingPin } = generatePinOTPMutation;

    const verifyPinMutation = useMutation({
        mutationFn: useAccountRetrievalVerifyOTPMutationFn,
        onSuccess: (data: any, variables) => {
            saveVerifiedOtp(variables.otp);
            saveVerifiedParentalPin(data?.parentalLockPin);
            setSuccessMsg(data.message);
            setStepValue(ParentalPinStep.VERIFY_OTP_FULFILLED);
            redirectUser();
        },
        onError: (error: any) => {
            setStepValue(ParentalPinStep.VERIFY_OTP_REJECTED);
            setErrMsg(null);
            const toastErrorCode = getParentalLockOtpToastErrorCode(error);
            setErrCode(toastErrorCode);
            toastCmsError(toastErrorCode);
        },
    });
    const { mutate: verifyOTP, data: verifyOTPData, isPending: isVerifyingPin } = verifyPinMutation;

    const resetGenerateOTPMutation = useMutation({
        mutationFn: useResetParentalLockGenerateOTPMutationFn,
        onSuccess: (data: any) => {
            if (data?.verificationGuid) {
                saveVerificationGuid(data.verificationGuid);
            }
            otpGenerateStartedRef.current = true;
            persistParentalLockOtpResendCooldown();
            setOtpResendCooldownSec(getParentalLockOtpResendCooldownSec());
            setOtpGenerateKey((current) => (current ?? 0) + 1);
            setStepValue(ParentalPinStep.RESET_GENERATE_OTP_FULFILLED);
            if (isOtpResendRef.current) {
                toast(__('parental_pin_management_resend_otp_success'), 'success');
            }
            isOtpResendRef.current = false;
        },
        onError: (error: any) => {
            isOtpResendRef.current = false;
            const toastErrorCode = getParentalLockOtpToastErrorCode(error);

            if (isCannotGenerateParentalLockOtpError(error)) {
                persistParentalLockOtpResendCooldown();
                const remainingSec = getParentalLockOtpResendCooldownRemainingSec();
                setOtpResendCooldownSec(remainingSec);
                setOtpGenerateKey((current) => (current ?? 0) + 1);
                setErrCode(toastErrorCode || 42007);
                setErrMsg(null);
                toastCmsError(toastErrorCode || 42007);

                if (getVerificationGuid()) {
                    setStepValue(ParentalPinStep.RESET_GENERATE_OTP_FULFILLED);
                }
                return;
            }

            setErrMsg(null);
            setErrCode(toastErrorCode);
            toastCmsError(toastErrorCode);
            removeVerifiedOtp();
            removeVerifiedParentalPin();
            removeVerificationGuid();

            const fallbackScreen = getParentalLockOtpFallbackScreen(getUserInfo(), user.getMSISDN());
            setShowPasswordPopup(fallbackScreen === parentalPinManagementConstants.CHANGE);
            setCurrentScreenType(fallbackScreen);
            navigate(
                url.route('parental-pin-management', {
                    type: window.btoa(
                        `${comesFromWhichScreen || parentalPinManagementConstants.KID_EXIT}|${fallbackScreen}`
                    ),
                }),
                { replace: true }
            );
        },
    });
    const { mutate: resetGenerateOTP, data: resetOtpData, isPending: isResetGenerating } = resetGenerateOTPMutation;

    const resetVerifyOTPMutation = useMutation({
        mutationFn: useResetParentalLockVerifyOTPMutationFn,
    });
    const { mutate: resetVerifyOTP, isPending: isResetVerifying } = resetVerifyOTPMutation;

    const resetWithOTPMutation = useMutation({
        mutationFn: useResetParentalLockWithOTPMutationFn,
    });
    const { mutate: resetWithOTP, isPending: isResetting } = resetWithOTPMutation;

    const navigateToParentalPinScreen = (fromScreen: string, screenType: string, replace = false) => {
        navigate(
            url.route('parental-pin-management', {
                type: window.btoa(`${fromScreen}|${screenType}`),
            }),
            replace ? { replace: true } : undefined
        );
    };

    const handleResetOtpVerify = (otp: string) => {
        const verificationGuid = getVerificationGuid();

        if (!verificationGuid) {
            setErrMsg('parental_pin_management_otp_error_general');
            setErrCode(null);
            toast(__('parental_pin_management_otp_error_general'), 'error');
            return;
        }

        saveVerifiedOtp(otp);
        resetVerifyOTP(
            { otp, verificationGuid },
            {
                onSuccess: (data) => {
                    saveVerifiedParentalPin(data?.parentalLockPin);
                    setErrMsg(null);
                    setErrCode(null);
                    setStepValue(ParentalPinStep.RESET_VERIFY_OTP_FULFILLED);
                    navigateToParentalPinScreen(
                        comesFromWhichScreen || parentalPinManagementConstants.KID_EXIT,
                        parentalPinManagementConstants.PARENTAL_LOCK_RESET_PIN,
                        true
                    );
                },
                onError: (error: any) => {
                    removeVerifiedOtp();
                    removeVerifiedParentalPin();
                    const toastErrorCode = getParentalLockOtpToastErrorCode(error);
                    setErrMsg(null);
                    setErrCode(toastErrorCode);
                    toastCmsError(toastErrorCode);
                },
            }
        );
    };

    const handleResetWithOtp = (
        pin: string,
        options: { verificationGuid?: string; currentParentalLockPin?: string }
    ) => {
        resetWithOTP(
            { pin, ...options },
            {
                onSuccess: () => {
                    removeVerifiedOtp();
                    removeVerifiedParentalPin();
                    removeVerificationGuid();
                    toast(__('parental_pin_management_reset_success'), 'success');
                    setStepValue(ParentalPinStep.RESET_WITH_OTP_FULFILLED);
                    redirectUser(ParentalPinStep.RESET_WITH_OTP_FULFILLED);
                },
                onError: (error: any) => {
                    const toastErrorCode = getParentalLockOtpToastErrorCode(error);
                    setErrMsg(null);
                    setErrCode(toastErrorCode);
                    toastCmsError(toastErrorCode);
                    setInputValue('');
                },
            }
        );
    };

    // Params not found error template.
    const paramNotFound = (
        <section className="watchlist-main">
            <div className="watchlist-block">
                <span className="watchlist-icon">
                    <img src="images/main-watchlist_icon.svg" alt="" />
                </span>
                <span className="watchlist-desc">{__('param_not_found')}</span>
            </div>
        </section>
    );

    //  set input value
    const onChange = (value) => setInputValue(value);
    const isLoadingAny =
        isLoadingGetPin ||
        isUpdating ||
        isGeneratingPin ||
        isVerifyingPin ||
        isResetGenerating ||
        isResetVerifying ||
        isResetting;
    // on submit button clicked
    const submitHandler = () => {
        const value = inputValue.replace(/\s/g, '');

        if (!value || value.length !== OTPLength) {
            return;
        }

        if (currentScreenType === parentalPinManagementConstants.ADD) {
            createPin({ pin: inputValue });
        } else if (currentScreenType === parentalPinManagementConstants.VALIDATE) {
            validatePin({ pin: inputValue });
        } else if (
            currentScreenType === parentalPinManagementConstants.CHANGE &&
            passwordValidated &&
            pin !== inputValue
        ) {
            updatePin({ password: userPassword, pin: inputValue });
        } else if (
            currentScreenType === parentalPinManagementConstants.CHANGE &&
            passwordValidated &&
            pin === inputValue
        ) {
            redirectUser();
        } else if (currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE) {
            verifyOTP({
                otp: inputValue,
                verificationGuid: generateOtpData?.verificationGuid,
                userId: generateOtpData?.userId,
                productKey: generateOtpData?.productKey,
            });
        } else if (currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_PIN) {
            const guid = getVerificationGuid();
            if (!guid) {
                setErrMsg('parental_pin_management_otp_error_general');
                toast(__('parental_pin_management_otp_error_general'), 'error');
                return;
            }
            handleResetWithOtp(inputValue, { verificationGuid: guid });
        } else if (currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_PIN_VIEW) {
            const currentParentalLockPin =
                getVerifiedParentalPin() || normalizeVerifiedParentalLockPin(verifyOTPData?.parentalLockPin);
            if (user.getParentalLockOn() && currentParentalLockPin) {
                handleResetWithOtp(inputValue, { currentParentalLockPin });
            } else {
                createPin({ pin: inputValue });
            }
        }
    };

    async function updateCartAndRedirect() {
        await user.initiatedUserProfiles(true);

        // Set showWelcomeScreen to true for kids profile after PIN validation
        // This ensures the gamification welcome modal appears on kids-home
        const selectedProfile = get(STORE_NAME.SELECTED_PROFILE);
        if (selectedProfile && selectedProfile.kidsMode) {
            const updatedProfile = {
                ...selectedProfile,
                showWelcomeScreen: true,
            };
            set(STORE_NAME.SELECTED_PROFILE, updatedProfile);
            dispatch(selectProfileDone(updatedProfile));
        }

        navigate(url.route('kids-home'), { replace: true });
    }

    useEffect(() => {
        createPinMutation.reset();
        validatePinMutation.reset();
        getPinMutation.reset();
        updatePinMutation.reset();
        generatePinOTPMutation.reset();
        verifyPinMutation.reset();
        resetGenerateOTPMutation.reset();
        resetVerifyOTPMutation.reset();
        resetWithOTPMutation.reset();
    }, []);

    // redirect after add/change/validate
    async function redirectUser(_stepValue?: string) {
        const stepVal = _stepValue || step;
        if (stepVal === ParentalPinStep.GET_FULFILLED && currentScreenType === parentalPinManagementConstants.CHANGE) {
            setCurrentScreenType(parentalPinManagementConstants.ACCOUNT_RETRIEVAL_PIN_VIEW);
        } else if (
            stepVal !== ParentalPinStep.GENERATE_OTP_FULFILLED &&
            stepVal !== ParentalPinStep.RESET_GENERATE_OTP_FULFILLED &&
            stepVal !== ParentalPinStep.RESET_VERIFY_OTP_FULFILLED &&
            comesFromWhichScreen === parentalPinManagementConstants.KID_EXIT
        ) {
            exitKidsMode(true);
        } else if (comesFromWhichScreen === parentalPinManagementConstants.KID_HOME) {
            // WILL REDIRECT TO KIDS HOME PAGE
            updateCartAndRedirect();
        } else if (comesFromWhichScreen === parentalPinManagementConstants.ACCOUNT) {
            // TODO : React Router (useNavigate()) is not working for external links.
            // navigate(user.getMyAccountUrl(), {replace: true});

            // Implementing this while investigating.
            window.location.replace(user.getMyAccountUrl());
        } else if (comesFromWhichScreen === parentalPinManagementConstants.CONSENT_MANAGEMENT) {
            const defaultProfile =
                profileList?.profiles?.find((profile) => profile.default === true && profile.kidsDefault === false) ||
                {};
            await h.setContext({ selectedProfile: defaultProfile });
            await h.setStore(STORE_NAME.SELECTED_PROFILE, defaultProfile);
            navigate(url.route(''));
        }
    }

    // enable/disable button a/c to input change
    useEffect(() => {
        const value = inputValue.replace(/\s/g, '');
        // if input length equals [OTPLength] then button will be enabled else disabled.
        if (value.length === OTPLength) {
            setButtonDisabled(false);
        } else {
            setButtonDisabled(true);
        }
        if (currentScreenType === parentalPinManagementConstants.CHANGE) {
            setButtonText(
                pin && pin === value
                    ? __(`parental_pin_management_btn_${currentScreenType}_pin`)
                    : __(`parental_pin_management_save_changes_btn`)
            );
        }
    }, [inputValue, currentScreenType, pin]);

    // get and validate parameters from URL
    useEffect(() => {
        setInputValue('');
        setPasswordValidated(false);
        // if params not found in the url or invalid, this will be triggered an error template. else store the value in the state.
        try {
            const decodeValue = window.atob(url.getQuery('type'));
            const splittedParams = decodeValue.split('|');
            const screenType = splittedParams[1];
            if (
                decodeValue &&
                decodeValue.length > 0 &&
                splittedParams.length === 2 &&
                h.getParentalPinPossibleScreens(screenType)
            ) {
                if (screenType === parentalPinManagementConstants.SET) {
                    setCurrentScreenType(
                        user.getParentalLockOn()
                            ? parentalPinManagementConstants.CHANGE
                            : parentalPinManagementConstants.ADD
                    );
                } else {
                    setCurrentScreenType(screenType);
                }
                setComesFromWhichScreen(splittedParams[0]);
            } else {
                setCurrentScreenType(parentalPinManagementConstants.NOT_FOUND);
            }
        } catch (e) {
            setCurrentScreenType(parentalPinManagementConstants.NOT_FOUND);
        }

        return () => {
            // clear input field intially
            setPasswordValidated(false);
            setInputValue('');
        };
    }, [location.search]);

    // if found change pin screen
    useEffect(() => {
        if (currentScreenType === parentalPinManagementConstants.CHANGE) {
            setButtonDisabled(false);
            setButtonText(
                pin
                    ? __('parental_pin_management_save_changes_btn')
                    : __(`parental_pin_management_btn_${currentScreenType}_pin`)
            );
        } else if (currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE) {
            if (isB2C) {
                setOTPLength(B2C_ACCOUNT_RETRIEVAL_OTP_LENGTH);
            }
            generateOTP();
        } else if (currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_OTP) {
            const storedGuid = readVerificationGuid() || verificationGuidRef.current;
            if (storedGuid) {
                saveVerificationGuid(storedGuid);
                otpGenerateStartedRef.current = true;
                const remainingSec = getParentalLockOtpResendCooldownRemainingSec();
                if (remainingSec > 0) {
                    setOtpResendCooldownSec(remainingSec);
                    setOtpGenerateKey((current) => (current ?? 0) + 1);
                }
                setResetOtpValue('');
                setResetOtpFocus(true);
                setStepValue(ParentalPinStep.RESET_GENERATE_OTP_FULFILLED);
                resetGenerateOTPMutation.reset();
                setErrCode(42007);
                setErrMsg(null);
                toastCmsError(42007);
            } else if (!otpGenerateStartedRef.current) {
                otpGenerateStartedRef.current = true;
                resetGenerateOTP();
            }
        }

        if (currentScreenType === parentalPinManagementConstants.CHANGE) {
            setShowPasswordPopup(true);
        } else if (
            currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE ||
            currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_OTP ||
            currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_PIN
        ) {
            setShowPasswordPopup(false);
        }
    }, [currentScreenType, isB2C]);

    useEffect(() => {
        if (currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_OTP && (errCode || errMsg)) {
            setResetOtpValue('');
            setResetOtpFocus(true);
        }
    }, [errCode, errMsg, currentScreenType]);

    useEffect(() => {
        if (successMsg) {
            setTimeout(() => {
                setErrMsg(false);
                setSuccessMsg(false);
                setErrCode(false);
            }, 1000);
        }
    }, [successMsg]);

    // setting parental pin to the state.
    useEffect(() => {
        if (typeof pin === 'string') setInputValue(pin);
    }, [pin]);

    useEffect(() => {
        if (step === ParentalPinStep.RESET_GENERATE_OTP_FULFILLED) {
            setOTPLength(resetOtpData?.otpLength || 4);
        } else if (step === ParentalPinStep.RESET_VERIFY_OTP_FULFILLED) {
            setOTPLength(4);
            setInputValue('');
            setCurrentScreenType(parentalPinManagementConstants.PARENTAL_LOCK_RESET_PIN);
        } else if (step === ParentalPinStep.GENERATE_OTP_FULFILLED) {
            if (currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE && isB2C) {
                // B2C account-retrieval OTP response has no `otpLength`, backend always expects 6 digits here.
                setOTPLength(B2C_ACCOUNT_RETRIEVAL_OTP_LENGTH);
            } else {
                setOTPLength(generateOtpData?.otpLength);
            }
        } else if (step === ParentalPinStep.VERIFY_OTP_FULFILLED) {
            if (isB2C && !verifyOTPData?.parentalLockPin) {
                // B2C OTP verification only confirms identity, there's no existing pin to retrieve/decode.
                // Let the user set a brand new pin on the following screen.
                setOTPLength(4);
                setInputValue('');
                setButtonText(__('parental_pin_management_btn_add_pin'));
            } else {
                const pinCode = window.atob(verifyOTPData?.parentalLockPin);
                setOTPLength(pinCode.length);
                setInputValue(pinCode);
                setButtonText(__(`parental_pin_management_save_changes_btn`));
            }
            setCurrentScreenType(parentalPinManagementConstants.ACCOUNT_RETRIEVAL_PIN_VIEW);
        } else if (step === ParentalPinStep.GET_FULFILLED) {
            setShowPasswordPopup(false);
            setPasswordValidated(true);
            setInputValue(pin);
        }
    }, [step, verifyOTPData, generateOtpData, resetOtpData, currentScreenType, isB2C]);

    const isParentalLockResetPinScreen = currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_PIN;

    const otpContact = getParentalLockOtpContact(getUserInfo(), user.getUsername(), user.getMSISDN());
    const maskedOtpContact = getMaskedOtpContact(otpContact);
    const otpActivateDescKey = `parental_pin_management_otp_activate_desc_${
        otpContact?.type === 'email' ? 'email' : 'sms'
    }`;
    const resetOtpLoading = isResetVerifying || isResetGenerating;

    const handlePinScreenCancel = async () => {
        removeVerificationGuid();
        if (!isParentalPinRequired()) {
            await clearSelectedProfile();
        }
        navigate(url.route(url.getDetailsPage(screenDetailTypes.PROFILE_SELECT)));
    };

    const onForgotPin = () => {
        const screenType = canUseParentalLockResetOtpFlow(getUserInfo())
            ? parentalPinManagementConstants.PARENTAL_LOCK_RESET_OTP
            : parentalPinManagementConstants.CHANGE;

        setErrMsg(null);
        setErrCode(null);
        setInputValue('');
        if (screenType !== parentalPinManagementConstants.PARENTAL_LOCK_RESET_OTP) {
            removeVerificationGuid();
        }
        removeVerifiedOtp();
        removeVerifiedParentalPin();
        setCurrentScreenType(screenType);
        setShowPasswordPopup(false);

        navigateToParentalPinScreen(parentalPinManagementConstants.KID_EXIT, screenType);
    };

    const handleOtpResendCooldownEnd = () => {
        clearParentalLockOtpResendCooldown();
        setOtpResendCooldownSec(0);
    };

    const handleResendParentalLockOtp = () => {
        setErrMsg(null);
        setErrCode(null);
        isOtpResendRef.current = true;
        resetGenerateOTP();
    };

    const useSkipParentalPin = useMutation({
        mutationFn: async ({
            guid,
            password,
            profileName,
            updatedProfileSettings,
            kidsMode,
            currentProfile,
        }: {
            guid: string;
            password: string | null;
            profileName: string;
            updatedProfileSettings: any;
            kidsMode: boolean | null;
            currentProfile: any;
        }) => {
            const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
            const user = getUserInfo();

            return await profileService.updateProfile(
                user.apiKey,
                guid,
                password,
                profileName,
                updatedProfileSettings,
                kidsMode,
                user.authToken,
                user.authSign,
                user.identityToken,
                currentProfile
            );
        },
        onSuccess: () => {
            client.invalidateQueries({ queryKey: ['profiles'] });
            navigate(url.route('kids-home'));
        },
    });

    async function handleParentalPinSkipButton() {
        // If the user clicked on the "Skip" button, this function will update the current kids profile and set the parental lock to "false"
        const storedProfile = user.getProfile();
        // The selected profile can be gone (cleared on a profile-switch error, or iOS storage eviction
        // while the session cookie survives); the user has to pick a profile again.
        if (!storedProfile?.profileGuid) {
            navigate(url.route(ROUTE_CONSTANT.SELECT_PROFILE), { replace: true });
            return;
        }
        const currentProfile = { ...storedProfile, parentalLockOn: false };
        h.setStore(STORE_NAME.SELECTED_PROFILE, currentProfile);
        const {
            profileName,
            avatar,
            newParentalControl,
            defaultAudioLang,
            passwordProtected,
            defaultSubTitleLang,
            autoPlayNextEpisode,
        } = currentProfile;
        const updatedProfileData = {
            profileName,
            profileSettings: {
                avatar,
                newParentalControl,
                defaultAudioLang,
                passwordProtected,
                defaultSubTitleLang,
                autoPlayNextEpisode,
                parentalLockOn: false,
            },
        };
        useSkipParentalPin.mutate({
            guid: currentProfile?.profileGuid,
            password: null,
            profileName: profileName,
            updatedProfileSettings: updatedProfileData.profileSettings,
            kidsMode: currentProfile.kidsMode !== true ? true : null,
            currentProfile,
        });
    }

    // Because some localization on B2B is different, for example "Password" should be "Account PIN" on B2B
    // If account type is B2B, Then this will be used to add "b2b_" just before the localization key
    const B2B_localization = isB2B ? 'b2b_' : '';

    return (
        <div id="wrapper" style={{ height: '100vh' }}>
            <HeaderWcp
                logoProps={{
                    homeUrl: '/',
                    title: 'STC logo',
                    logoSrc: 'images/big-logo.svg',
                    LinkComponent: 'a',
                }}
            />
            {successMsg && comesFromWhichScreen === parentalPinManagementConstants.ACCOUNT && (
                <div className={`profile-form_notification`}>
                    <p className={'profile-form_notiTitle'}>
                        <span>{successMsg}</span>
                    </p>
                </div>
            )}
            {currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_OTP && (
                <div className="container" style={{ paddingBottom: 'calc(var(--step) * 18)' }}>
                    <PinVerification
                        key={`parental-lock-otp-cooldown-${otpGenerateKey ?? 0}`}
                        handleSubmit={(pin) => {
                            if (!pin) {
                                return;
                            }
                            setResetOtpFocus(false);
                            handleResetOtpVerify(pin);
                        }}
                        handleResendPinCode={() => {
                            if (resetOtpLoading) {
                                return;
                            }
                            setResetOtpValue('');
                            handleResendParentalLockOtp();
                        }}
                        isLoading={resetOtpLoading}
                        maxPinLength={OTPLength || 4}
                        mobileNumber={maskedOtpContact}
                        title={__('parental_pin_management_otp_header')}
                        subtitle={__('parental_pin_management_otp_desc')}
                        setPin={(pin) => {
                            setResetOtpValue(pin);
                            if (pin.length === OTPLength) {
                                setResetOtpFocus(false);
                                handleResetOtpVerify(pin);
                            }
                        }}
                        countDownSeconds={otpResendCooldownSec}
                        promptMessage={__(otpActivateDescKey)}
                        pin={resetOtpValue}
                        resendText={__('parental_pin_management_resend_otp')}
                        resendWaitText={__('parental_pin_management_resend_otp_wait')}
                        shouldNotShowEditButton
                        hideSubmitButton
                        shouldAutoFocus={resetOtpFocus}
                        onCountdownEnd={handleOtpResendCooldownEnd}
                    />
                </div>
            )}
            {currentScreenType &&
                currentScreenType !== parentalPinManagementConstants.NOT_FOUND &&
                currentScreenType !== parentalPinManagementConstants.PARENTAL_LOCK_RESET_OTP && (
                    <div className="container" style={{ paddingBottom: 'calc(var(--step) * 18)' }}>
                        <Text as="h2" variant="h2" margin="4x" alignment="center">
                            {currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE
                                ? __('parental_pin_management_otp_generate_header')
                                : currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_PIN
                                ? __('parental_pin_management_title_change_pin')
                                : currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_PIN_VIEW
                                ? __('parental_pin_management_title_add_pin')
                                : __(`parental_pin_management_title_${currentScreenType}_pin`)}
                        </Text>
                        <FlexWidth direction="column" gap="4x" alignItems="center" presetWidth="small">
                            <Text as="span" size="14" style={{ color: 'var(--white-30)' }}>
                                {currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE
                                    ? __('parental_pin_management_otp_generate_desc')
                                    : currentScreenType === parentalPinManagementConstants.PARENTAL_LOCK_RESET_PIN
                                    ? __('parental_pin_management_desc_change_pin')
                                    : currentScreenType === parentalPinManagementConstants.ACCOUNT_RETRIEVAL_PIN_VIEW
                                    ? __('parental_pin_management_desc_add_pin')
                                    : __(`parental_pin_management_desc_${currentScreenType}_pin`)}
                            </Text>
                            <PinHolder
                                value={inputValue}
                                numInputs={OTPLength}
                                onChange={onChange}
                                shouldAutoFocus={true}
                                skipDefaultStyles
                                inputType="tel"
                                renderInput={(inputProps, index) => {
                                    const { onKeyDown, ...restInputProps } = inputProps;
                                    return (
                                        <input
                                            key={index}
                                            {...restInputProps}
                                            autoComplete="one-time-code"
                                            onKeyDown={(event) => {
                                                onKeyDown?.(event);
                                                if (
                                                    !event.defaultPrevented &&
                                                    event.key === 'Enter' &&
                                                    inputValue.length === OTPLength &&
                                                    !isValidating
                                                ) {
                                                    submitHandler();
                                                }
                                            }}
                                        />
                                    );
                                }}
                            />
                            {currentScreenType === parentalPinManagementConstants.VALIDATE && (
                                <>
                                    {isMobilityUser && (
                                        <a onClick={onForgotPin} className="primary-brand">
                                            {__(`parental_pin_management_forgot_pin`)}
                                        </a>
                                    )}
                                    {
                                        // Visible for B2B and B2B2C
                                        !isB2C && (
                                            <a
                                                onClick={() => {
                                                    setCurrentScreenType(parentalPinManagementConstants.CHANGE);
                                                    navigateToParentalPinScreen(
                                                        parentalPinManagementConstants.KID_EXIT,
                                                        parentalPinManagementConstants.CHANGE
                                                    );
                                                }}
                                                className="primary-brand"
                                            >
                                                {__(B2B_localization + 'use_password_instead')}
                                            </a>
                                        )
                                    }
                                </>
                            )}
                            {h.isAccountRetrievalFeatureEnabled() &&
                                currentScreenType === parentalPinManagementConstants.ADD && (
                                    <a
                                        href={
                                            user.getProfileMode() === KIDSMODE.KID
                                                ? url.route('kids-home')
                                                : url.route('')
                                        }
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            handleParentalPinSkipButton();
                                        }}
                                        className="primary-brand"
                                    >
                                        {__('parental_pin_management_form_skip')}
                                    </a>
                                )}
                            <FlexWrapper itemCount={2} gap="2x" style={{ width: '100%' }}>
                                <Button fullWidth onClick={handlePinScreenCancel} data-test="pin-cancel">
                                    {__(`parental_pin_management_cancel_btn`)}
                                </Button>
                                <Button
                                    primary
                                    onClick={submitHandler}
                                    disabled={buttonDisabled || isValidating}
                                    loading={
                                        isValidating ||
                                        (isParentalLockResetPinScreen
                                            ? isResetting
                                            : currentScreenType ===
                                                  parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE &&
                                              isGeneratingPin)
                                    }
                                    data-test="pin-submit"
                                >
                                    {isParentalLockResetPinScreen
                                        ? __('parental_pin_management_save_changes_btn')
                                        : buttonText ||
                                          (currentScreenType ===
                                          parentalPinManagementConstants.ACCOUNT_RETRIEVAL_PIN_VIEW
                                              ? __('parental_pin_management_btn_add_pin')
                                              : __(`parental_pin_management_btn_${currentScreenType}_pin`))}
                                </Button>
                            </FlexWrapper>
                        </FlexWidth>
                    </div>
                )}
            {!isLoadingAny &&
                currentScreenType &&
                currentScreenType === parentalPinManagementConstants.NOT_FOUND &&
                paramNotFound}
            <PasswordPopup
                showPopup={showPasswordPopup}
                onSuccess={(pass) => getPin(pass)}
                canClose={false}
                title={__(B2B_localization + 'parental_pin_management_password_title')}
                description={__(B2B_localization + 'parental_pin_management_password_desc')}
                cancelButtonText={__('parental_pin_management_password_cancel_btn')}
                buttonText={__('parental_pin_management_password_submit_btn')}
                onClose={() => navigate(NAVIGATION.GOBACKTO_PREVIOUS_SCREEN)}
            />
        </div>
    );
};
